import { FC, useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// REDUX
import { useAppDispatch, useAppSelector } from '../../../store/slice';
import { ToastMessagesSlice } from '../../../store/slice/ToastMessages';

// API
import apibridge from '../../../apibridge';

// UTILS
import { guid } from '../../../libs/utils';

// PACKAGES
import { Modal, Offcanvas, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useImmer } from 'use-immer';

// COMPONENTS
import MarkingPill from '../../../components/Assessment/MarkingPill';
import PaginationDot from '../../../components/Assessment/PaginationDot';
import GraphemeQuestion from '../../../components/Assessment/GraphemeQuestion';
import DelayedFadeIn from '../../../components/_Helpers/DelayedFadeIn';
import SvgMask from '../../../components/_Helpers/SvgMask';

// TYPES
import { AssessmentAnswerStatus, AssessmentEditLlarsAssessmentInfo } from '../../../api/models';
import { AsessmentConductSlice, AsessmentConductType } from '../../../store/slice/AssessmentConduct';
import { UserType } from '../../../store/slice/User';

const ConductAssessment: FC = () => {
	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	// Get the position from Redux store
	const recordPosition: AsessmentConductType = useAppSelector((state) => state.assessmentConduct);
	const recordStageId = recordPosition?.recordStageId;
	const recordQuestionId = recordPosition?.recordQuestionId;

	// Get Details from params
	const [searchParams] = useSearchParams();
	const recordId = searchParams.get('recordId') || '';
	const assessmentName = searchParams.get('assessmentName') || '';

	// Loading State
	const [isLoading, setIsLoading] = useState(false);

	// State to hide/show Canvas and Modal
	const [showStageSelectionOffCanvas, setShowStageSelectionOffCanvas] = useState(false);
	const [showSaveAssessmentModal, setShowSaveAssessmentModal] = useState(false);

	// Assessment Details
	const [assessmentReadingDetailsData, setAssessmentReadingDetailsData] = useImmer<AssessmentEditLlarsAssessmentInfo>(
		{}
	);

	// Clone state of Assessment Details
	const [assessmentReadingDetailsDataClone, setAssessmentReadingDetailsDataClone] =
		useImmer<AssessmentEditLlarsAssessmentInfo>({});

	// Get Details call
	const getAssessmentReadingDetailsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAssessmentEditLlars(recordId);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAssessmentReadingDetailsData(response.data.result);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Details error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	useEffect(() => {
		if (recordId) {
			getAssessmentReadingDetailsData();
		}
	}, [recordId]); // eslint-disable-line react-hooks/exhaustive-deps

	const { firstName, lastName, className, stages, showAttributes, incorrectNoteTypes, currentMasteryStageId } =
		assessmentReadingDetailsData || {};

	const confirmSaveAssessment = async (displayNotification?: boolean) => {
		const submitData = {
			recordId,
			recordingDate: new Date().toISOString(),
			stages: assessmentReadingDetailsData?.stages?.map((stageItem) => ({
				id: stageItem?.id,
				comment: stageItem?.comment,
				answers: stageItem?.questions?.map((questionItem) => ({
					id: questionItem?.id,
					usedDecoding: questionItem?.usedDecoding,
					alternativePronunciation: questionItem?.alternativePronunciation,
					incorrectNoteType: questionItem?.incorrectNoteType,
					graphemes: questionItem?.graphemes?.map((graphemeItem) => ({
						id: graphemeItem?.id,
						status: graphemeItem?.status
					}))
				}))
			}))
		};

		const response = await apibridge.postAssessmentEditLlars(submitData);

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				// Set the clone state to the current state.
				// This is used to revert changes when user click Discard Changes button
				setAssessmentReadingDetailsDataClone(assessmentReadingDetailsData);

				// Only display Toast Message if displayNotification is true
				if (displayNotification) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'success',
							heading: 'Record saved.',
							description: ''
						})
					);
				}
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Assessment Submission error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}
	};

	// Logic to load correct stage and question
	const [currentStage, setCurrentStage] = useState(stages?.[0]);

	const [currentQuestion, setCurrentQuestion] = useState(currentStage?.questions?.[0]);

	// If there is a currentMasteryStageId, then update the position in REDUX
	// The Logic needs to only run ONCE when mounted
	// Could not use useRef true/false here because react-router will persit the ref, use location.key instead
	const lastLocationKey = useRef<string | null>(null);

	useEffect(() => {
		if (currentMasteryStageId && stages && stages.length > 0 && lastLocationKey.current !== location.key) {
			const currentMasteryStage = stages.find((stageItem) => stageItem.id === currentMasteryStageId);

			dispatch(
				AsessmentConductSlice.actions.updatePosition({
					recordStageId: currentMasteryStage?.id
				})
			);

			lastLocationKey.current = location.key;
		}
	}, [currentMasteryStageId, stages, location.key]);

	useEffect(() => {
		// Use REDUX Logic to determine currentStage
		if (recordStageId) {
			const currentStagePosition = stages?.find((stageItem) => stageItem.id === recordStageId);

			setCurrentStage(currentStagePosition || stages?.[0]);
		} else {
			setCurrentStage(stages?.[0]);
		}
	}, [stages, recordStageId, currentMasteryStageId]);

	useEffect(() => {
		// Use REDUX Logic to determine currentQuestion
		if (recordQuestionId) {
			const currentQuestionPosition = currentStage?.questions?.find(
				(questionItem) => questionItem.id === recordQuestionId
			);

			setCurrentQuestion(currentQuestionPosition || currentStage?.questions?.[0]);
		} else {
			setCurrentQuestion(currentStage?.questions?.[0]);
		}
	}, [currentStage, recordQuestionId]);

	// Logic for displaying the correct buttons
	// Index of the current Stage
	const currentStageIndex = stages?.findIndex((stageItem) => stageItem.id === currentStage?.id) || 0;

	// Index of the current Question
	const currentQuestionIndex =
		currentStage?.questions?.findIndex((question) => question.id === currentQuestion?.id) || 0;

	const isFirstStage = currentStageIndex === 0;

	const isLastStage = currentStageIndex + 1 === stages?.length;

	const isFirstQuestion = currentQuestionIndex === 0;

	const isLastQuestion = currentQuestionIndex + 1 === currentStage?.questions?.length;

	// Update the status of the question before navigating starts
	const updateQuestionStatus = () => {
		setAssessmentReadingDetailsData((draft) => {
			const draftStage = draft?.stages?.find((draftStageItem) => draftStageItem.id === currentStage?.id);

			const draftQuestion = draftStage?.questions?.find(
				(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
			);

			if (draftQuestion) {
				// If All Phonemes has been marked, AND at least ONE Phonemes is wrong, then change the Question Status to Unsuccessul
				if (
					currentQuestion?.graphemes?.every((graphemeItem) => graphemeItem.status !== AssessmentAnswerStatus.Pending) &&
					currentQuestion?.graphemes?.some(
						(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
					)
				) {
					draftQuestion.status = AssessmentAnswerStatus.Unsuccessful;
				} else if (
					// If All Phonemes has been marked, AND ALL Phonemes are correct, then change the Question Status to Successul
					currentQuestion?.graphemes?.every((graphemeItem) => graphemeItem.status !== AssessmentAnswerStatus.Pending) &&
					currentQuestion?.graphemes?.every((graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Successful)
				) {
					draftQuestion.status = AssessmentAnswerStatus.Successful;
				}
			}
		});
	};

	// Logic to hide and show incorrect notes
	const showIncorrectNotes = currentQuestion?.graphemes?.some(
		(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
	);

	// Logic to reset incorrect notes value
	const resetIncorrectNotes = () => {
		setAssessmentReadingDetailsData((draft) => {
			const draftStage = draft?.stages?.find((draftStageItem) => draftStageItem.id === currentStage?.id);

			const draftQuestion = draftStage?.questions?.find(
				(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
			);

			if (draftQuestion) {
				draftQuestion.incorrectNoteType = undefined;
			}
		});
	};

	// IMPERSONATE LOGIC
	const isImpersonated = useAppSelector((state) => state.user.isImpersonated);
	const user: UserType = useAppSelector((state) => state.user);

	// Find active organisation
	const profiles = useAppSelector((state) => state.user.profiles);
	const activeProfileId = user.activeProfileId;
	const activeProfile = profiles?.find((profile) => profile.id === activeProfileId);
	const activeProfileName = activeProfile?.name;

	return (
		<>
			{isImpersonated && (
				<div className="imptersonating-banner">
					<SvgMask path={'/svg/profile.svg'} width={20} height={20} />

					<p className="m-0">
						Impersonating{' '}
						<strong>
							{user.firstName} {user.lastName}
						</strong>
						{activeProfileName && (
							<>
								{' '}
								for <strong>{activeProfileName}</strong>
							</>
						)}
					</p>
				</div>
			)}

			<div className="page-book-reader page-conduct-assessment bg-white">
				<button className="btn btn-home-corner assessment-home" onClick={() => setShowSaveAssessmentModal(true)}>
					<i></i>
					<span className="visually-hidden">Home</span>
				</button>

				<div className="position-absolute top-0 end-0 w-100 d-flex flex-column align-items-center gap-3">
					<p className="mt-4 mb-0 h4 text-dark-text">
						{firstName} {lastName} <span className="text-shades-500">{className}</span>{' '}
					</p>

					<div className="d-flex justify-content-center align-items-center gap-3">
						<p className="mb-0 h3 text-shades-800 fw-bold">LLARS</p>

						<p className="mb-0 h3 text-shades-800 fw-semibold">{assessmentName}</p>

						<div className={`pill stage-${currentStage?.stageName}`}>Stage {currentStage?.stageName}</div>
					</div>
				</div>

				<div className="position-absolute top-0 end-0 d-flex flex-column gap-3">
					<button
						type="button"
						className="btn btn-top-right btn-menu text-uppercase"
						onClick={() => setShowStageSelectionOffCanvas(true)}
					>
						<i></i>
						<span className="visually-hidden">Menu</span>
					</button>
				</div>

				{isLoading ? (
					<div className="d-flex justify-content-center w-100 my-5">
						<DelayedFadeIn>
							<Spinner />
						</DelayedFadeIn>
					</div>
				) : (
					<>
						<div className="d-flex flex-column justify-content-center align-items-center w-100 gap-5">
							{/* Questions */}
							<div className="d-flex justify-content-center flex-wrap gap-3">
								{currentQuestion?.graphemes?.map((graphemeItem, graphemeIndex) => {
									return (
										<GraphemeQuestion
											key={graphemeItem.id || graphemeIndex}
											label={graphemeItem.text || ''}
											state={
												graphemeItem.status === AssessmentAnswerStatus.Successful
													? 'success'
													: graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
													? 'danger'
													: 'default'
											}
											onClick={() => {
												// Update the status of the grapheme
												setAssessmentReadingDetailsData((draft) => {
													const draftStage = draft?.stages?.find(
														(draftStageItem) => draftStageItem.id === currentStage?.id
													);

													const draftQuestion = draftStage?.questions?.find(
														(draftQuestionItem) => draftQuestionItem.id === currentQuestion.id
													);

													const draftGrapheme = draftQuestion?.graphemes?.find(
														(draftGraphemeItem) => draftGraphemeItem.id === graphemeItem.id
													);

													if (draftGrapheme && draftGrapheme.status !== undefined) {
														if (
															draftGrapheme.status === AssessmentAnswerStatus.Unsuccessful ||
															draftGrapheme.status === AssessmentAnswerStatus.Pending
														) {
															draftGrapheme.status = AssessmentAnswerStatus.Successful;
														} else {
															draftGrapheme.status = AssessmentAnswerStatus.Unsuccessful;
														}
													}
												});

												// Reset the incorrect notes if the grapheme is marked as correct
												// This should only happen if the grapheme is the last Unsuccessful one AND the currentQuestion has incorrectNoteType
												if (
													graphemeItem.status === AssessmentAnswerStatus.Unsuccessful &&
													currentQuestion?.graphemes?.filter(
														(graphemeFilterItem) => graphemeFilterItem.status === AssessmentAnswerStatus.Unsuccessful
													).length === 1 &&
													currentQuestion.incorrectNoteType !== undefined
												) {
													resetIncorrectNotes();
												}

												// Update position in Redux
												dispatch(
													AsessmentConductSlice.actions.updatePosition({
														recordStageId: currentStage?.id,
														recordQuestionId: currentQuestion?.id
													})
												);
											}}
										/>
									);
								})}
							</div>

							{/* Answer */}
							<div className="d-flex gap-4">
								<MarkingPill
									state="incorrect"
									isActive={
										currentQuestion?.graphemes?.every(
											(graphemeItem) => graphemeItem.status !== AssessmentAnswerStatus.Pending
										) &&
										currentQuestion?.graphemes?.some(
											(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
										)
									}
									onClick={() => {
										// Update the status of the grapheme
										setAssessmentReadingDetailsData((draft) => {
											const draftStage = draft?.stages?.find(
												(draftStageItem) => draftStageItem.id === currentStage?.id
											);

											const draftQuestion = draftStage?.questions?.find(
												(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
											);

											draftQuestion?.graphemes?.forEach((graphemeItem) => {
												graphemeItem.status = AssessmentAnswerStatus.Unsuccessful;
											});

											if (draftQuestion && draftQuestion.status !== undefined) {
												draftQuestion.status = AssessmentAnswerStatus.Unsuccessful;
											}
										});

										// Update position in Redux
										dispatch(
											AsessmentConductSlice.actions.updatePosition({
												recordStageId: currentStage?.id,
												recordQuestionId: currentQuestion?.id
											})
										);
									}}
								/>

								<MarkingPill
									state="correct"
									isActive={
										currentQuestion?.graphemes?.every(
											(graphemeItem) => graphemeItem.status !== AssessmentAnswerStatus.Pending
										) &&
										currentQuestion?.graphemes?.every(
											(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Successful
										)
									}
									onClick={() => {
										// Update the status of the grapheme
										setAssessmentReadingDetailsData((draft) => {
											const draftStage = draft?.stages?.find(
												(draftStageItem) => draftStageItem.id === currentStage?.id
											);

											const draftQuestion = draftStage?.questions?.find(
												(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
											);

											draftQuestion?.graphemes?.forEach((graphemeItem) => {
												graphemeItem.status = AssessmentAnswerStatus.Successful;
											});

											if (draftQuestion && draftQuestion.status !== undefined) {
												draftQuestion.status = AssessmentAnswerStatus.Successful;
											}
										});

										// Reset the incorrect notes if it has values
										if (currentQuestion && currentQuestion.incorrectNoteType !== undefined) {
											resetIncorrectNotes();
										}

										// For Non-words, Marking all correct will Update the position in Redux
										if (showAttributes) {
											dispatch(
												AsessmentConductSlice.actions.updatePosition({
													recordStageId: currentStage?.id,
													recordQuestionId: currentQuestion?.id
												})
											);
										}

										// For Grapheme-Phoneme Knowledge, Move to the next question when marking All Correct, then Update the position in Redux
										// Only do this if it's NOT the last question in the stage
										if (!showAttributes) {
											if (!isLastQuestion) {
												const goToQuestion = currentStage?.questions?.[currentQuestionIndex + 1];

												dispatch(
													AsessmentConductSlice.actions.updatePosition({
														recordStageId: currentStage?.id,
														recordQuestionId: goToQuestion?.id
													})
												);
											}
										}
									}}
								/>
							</div>

							{/* Checkbox Attributes */}
							{showAttributes && (
								<div className="d-flex gap-3">
									<label
										className={`assessment-checkbox ${currentQuestion?.usedDecoding ? 'checked' : ''}`}
										htmlFor="attribute-decoded"
									>
										<div className="d-flex align-items-center gap-2">
											<input
												className="form-check"
												type="checkbox"
												name="attribute-decoded"
												id="attribute-decoded"
												checked={currentQuestion?.usedDecoding}
												onChange={() => {
													setAssessmentReadingDetailsData((draft) => {
														const draftStage = draft?.stages?.find(
															(draftStageItem) => draftStageItem.id === currentStage?.id
														);

														const draftQuestion = draftStage?.questions?.find(
															(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
														);

														if (draftQuestion) {
															if (currentQuestion?.usedDecoding === undefined) {
																draftQuestion.usedDecoding = true;
															} else {
																draftQuestion.usedDecoding = !currentQuestion?.usedDecoding;
															}
														}
													});
												}}
											/>
											<span className="h4">
												<strong>Decoded word</strong>
											</span>
										</div>
									</label>

									<label
										className={`assessment-checkbox ${currentQuestion?.alternativePronunciation ? 'checked' : ''}`}
										htmlFor="attribute-read-auto"
									>
										<div className="d-flex align-items-center gap-2">
											<input
												className="form-check"
												type="checkbox"
												name="attribute-read-auto"
												id="attribute-read-auto"
												checked={currentQuestion?.alternativePronunciation}
												onChange={() => {
													setAssessmentReadingDetailsData((draft) => {
														const draftStage = draft?.stages?.find(
															(draftStageItem) => draftStageItem.id === currentStage?.id
														);

														const draftQuestion = draftStage?.questions?.find(
															(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
														);

														if (draftQuestion) {
															if (currentQuestion?.alternativePronunciation === undefined) {
																draftQuestion.alternativePronunciation = true;
															} else {
																draftQuestion.alternativePronunciation = !currentQuestion?.alternativePronunciation;
															}
														}
													});
												}}
											/>
											<span className="h4">
												<strong>Read word automatically</strong>
											</span>
										</div>
									</label>
								</div>
							)}

							{/* Radio Button Incorrect Note */}
							<div className={`incorrect-note-wrapper ${showIncorrectNotes ? '' : 'disable'}`}>
								<h3 className="mb-3 text-center">Incorrect note</h3>

								<div className="d-flex flex-wrap justify-content-center gap-3">
									{incorrectNoteTypes?.map((noteTypeItem, noteTypeIndex) => {
										return (
											<label
												htmlFor={`incorrect-note-${noteTypeItem.value}`}
												className={`assessment-radio ${
													currentQuestion?.incorrectNoteType === noteTypeItem.value ? 'checked' : ''
												}`}
												key={noteTypeItem?.value || noteTypeIndex}
											>
												<div className="d-flex align-items-center gap-2">
													<input
														type="radio"
														id={`incorrect-note-${noteTypeItem.value}`}
														name={`incorrect-note-${noteTypeItem.value}`}
														className="form-check-input"
														checked={currentQuestion?.incorrectNoteType === noteTypeItem.value}
														onChange={() => {
															setAssessmentReadingDetailsData((draft) => {
																const draftStage = draft?.stages?.find(
																	(draftStageItem) => draftStageItem.id === currentStage?.id
																);

																const draftQuestion = draftStage?.questions?.find(
																	(draftQuestionItem) => draftQuestionItem.id === currentQuestion?.id
																);

																if (draftQuestion) {
																	if (!draftQuestion.incorrectNoteType) {
																		draftQuestion.incorrectNoteType = noteTypeItem.value;
																	} else {
																		draftQuestion.incorrectNoteType = noteTypeItem.value;
																	}
																}
															});
														}}
													/>
													<span className="h4">
														<strong>{noteTypeItem.label}</strong>
													</span>
												</div>
											</label>
										);
									})}
								</div>
							</div>
						</div>

						<div className="position-absolute bottom-0 end-0 mb-4 px-4 w-100 d-flex gap-3 justify-content-between align-items-center ">
							<div className="assessment-control-btn-wrapper prev">
								{/* Prev QUESTION Button */}
								{!isFirstQuestion && (
									<button
										type="button"
										className="btn btn-icon btn-arrow-left"
										onClick={() => {
											// Update the status of the question before navigating away
											updateQuestionStatus();

											// Find the question to go to
											const goToQuestion = currentStage?.questions?.[currentQuestionIndex - 1];

											dispatch(
												AsessmentConductSlice.actions.updatePosition({
													recordStageId: currentStage?.id,
													recordQuestionId: goToQuestion?.id
												})
											);
										}}
									>
										<span className="visually-hidden">Previous Question</span>
										<i></i>
									</button>
								)}

								{/* Prev STAGE Button */}
								{!isFirstStage && isFirstQuestion && (
									<button
										type="button"
										className="btn btn-prev-stage"
										onClick={() => {
											// Update the status of the question before navigating away
											updateQuestionStatus();

											// Find the Stage to go to
											const goToStage = stages?.[currentStageIndex - 1];

											dispatch(
												AsessmentConductSlice.actions.updatePosition({
													recordStageId: goToStage?.id,
													recordQuestionId: goToStage?.questions?.[0]?.id
												})
											);
										}}
									>
										<span className="visually-hidden">Previous Stage</span>
										Prev Stage
									</button>
								)}
							</div>

							{/* Pagination */}
							<div className="d-flex flex-wrap justify-content-center align-items-center gap-1">
								{currentStage?.questions?.map((questionItem, questionIndex) => {
									const isQuestionCorrect = questionItem?.graphemes?.every(
										(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Successful
									);

									const isQuestionIncorrect = questionItem?.graphemes?.some(
										(graphemeItem) => graphemeItem.status === AssessmentAnswerStatus.Unsuccessful
									);

									return (
										<PaginationDot
											key={questionItem.id || questionIndex}
											isActive={currentQuestion?.id === questionItem.id}
											label={questionIndex + 1}
											onClick={() => {
												// Update the status of the question before navigating away
												updateQuestionStatus();

												// Update the position in Redux
												dispatch(
													AsessmentConductSlice.actions.updatePosition({
														recordStageId: currentStage?.id,
														recordQuestionId: questionItem?.id
													})
												);
											}}
											state={isQuestionIncorrect ? 'danger' : isQuestionCorrect ? 'success' : 'default'}
										/>
									);
								})}
							</div>

							<div className="assessment-control-btn-wrapper next">
								{/* Next QUESTION Button */}
								{!isLastQuestion && (
									<button
										type="button"
										className="btn btn-icon btn-arrow-right"
										onClick={() => {
											// Update the status of the question before navigating away
											updateQuestionStatus();

											// Find the question to go to
											const goToQuestion = currentStage?.questions?.[currentQuestionIndex + 1];

											dispatch(
												AsessmentConductSlice.actions.updatePosition({
													recordStageId: currentStage?.id,
													recordQuestionId: goToQuestion?.id
												})
											);
										}}
									>
										<span className="visually-hidden">Next Question</span>
										<i></i>
									</button>
								)}

								{/* Next STAGE Button */}
								{!isLastStage && isLastQuestion && (
									<button
										type="button"
										className="btn btn-next-stage"
										onClick={() => {
											// Update the status of the question before navigating away
											updateQuestionStatus();

											// Find the Stage to go to
											const goToStage = stages?.[currentStageIndex + 1];

											dispatch(
												AsessmentConductSlice.actions.updatePosition({
													recordStageId: goToStage?.id,
													recordQuestionId: goToStage?.questions?.[0]?.id
												})
											);

											// Save the assessment everytime user move to the NEXT Stage
											confirmSaveAssessment();
										}}
									>
										<span className="visually-hidden">Next Stage</span>
										Next Stage
									</button>
								)}

								{/* Finish Button */}
								{isLastStage && isLastQuestion && (
									<button
										type="button"
										className="btn btn-icon btn-finish-assessment"
										onClick={() => {
											// Update the status of the question before navigating away
											updateQuestionStatus();

											confirmSaveAssessment(true);

											navigate(`/assessment-management/reading?recordId=${recordId}&assessmentName=${assessmentName}`);
										}}
									>
										<span className="visually-hidden">Finish</span>
										Finish
									</button>
								)}
							</div>
						</div>
					</>
				)}

				<Modal show={showSaveAssessmentModal} onHide={() => setShowSaveAssessmentModal(false)} centered>
					<Modal.Body>
						<h2 className="m-0 text-shades-800">
							<strong>Finish conducting assessment</strong>
						</h2>

						<p>
							Would you like to save changes made to <strong>{assessmentName}</strong> for{' '}
							<strong>
								{firstName} {lastName}
							</strong>{' '}
							?
						</p>

						<div className="d-flex flex-column gap-2">
							<button
								onClick={() => {
									// Update the status of the question before navigating away
									updateQuestionStatus();

									confirmSaveAssessment(true);

									navigate(`/assessment-management/reading?recordId=${recordId}&assessmentName=${assessmentName}`);
								}}
								className="btn btn-lg w-100"
							>
								<SvgMask path={'/svg/save-icon.svg'} width={16} height={16} />
								Save progress
							</button>

							<button
								onClick={() => {
									setAssessmentReadingDetailsData(assessmentReadingDetailsDataClone);

									navigate(`/assessment-management/reading?recordId=${recordId}&assessmentName=${assessmentName}`);
								}}
								className="btn btn-exit btn-lg w-100"
							>
								<SvgMask path={'/svg/trash-icon.svg'} width={16} height={16} /> Exit without saving
							</button>
						</div>

						<button onClick={() => setShowSaveAssessmentModal(false)} className="btn btn-white btn-lg w-100 mt-3">
							Cancel
						</button>
					</Modal.Body>
				</Modal>

				<Offcanvas
					bsPrefix="view-teacher offcanvas"
					show={showStageSelectionOffCanvas}
					onHide={() => setShowStageSelectionOffCanvas(false)}
					placement="end"
				>
					<Offcanvas.Header closeButton>
						<Offcanvas.Title className="d-flex flex-column justify-content-center align-items-center w-100 gap-1">
							<p className="text-dark-text mb-3">{assessmentName}</p>

							<div className="d-flex align-items-center gap-2">
								<h2 className="text-shades-800 mb-0 fw-semibold">Move to stage</h2>
							</div>
						</Offcanvas.Title>
					</Offcanvas.Header>

					<Offcanvas.Body className="d-flex flex-column">
						{isLoading ? (
							<div className="d-flex align-items-center justify-content-center w-100">
								<DelayedFadeIn>
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</DelayedFadeIn>
							</div>
						) : (
							<>
								<div className="form-selection-wrapper">
									<div className="form-selection-wrapper-inner">
										{stages?.map((stageItem, stageIndex) => {
											const totalQuestions = stageItem?.questions?.length;

											const markedQuestions =
												stageItem?.questions?.filter(
													(questionItem) => questionItem.status !== AssessmentAnswerStatus.Pending
												).length || 0;

											const isFullyMarked = markedQuestions === totalQuestions;

											const isStageActive = stageItem?.id === currentStage?.id;

											return (
												<button
													className={`btn btn-row-label ${isStageActive ? 'active' : ''}`}
													key={stageItem.id || stageIndex}
													onClick={() => {
														// Update the status of the question before navigating away
														updateQuestionStatus();

														// Move to the clicked stage
														setCurrentStage(stageItem);

														dispatch(
															AsessmentConductSlice.actions.updatePosition({
																recordStageId: stageItem?.id,
																recordQuestionId: stageItem?.questions?.[0]?.id
															})
														);

														setShowStageSelectionOffCanvas(false);
													}}
												>
													<strong>Stage {stageItem.stageName}</strong>

													<div className="d-flex align-items-center gap-2">
														<p className="mb-0 fw-bold">
															{markedQuestions}/{totalQuestions} Marked
														</p>

														{isFullyMarked ? (
															<div className="selected-icon-wrapper complete">
																<SvgMask path="/svg/check-solid-blue.svg" width={16} height={16} />
															</div>
														) : (
															<div className="selected-icon-wrapper">
																<SvgMask path="/svg/minus-outline.svg" width={16} height={16} />
															</div>
														)}
													</div>
												</button>
											);
										})}
									</div>
								</div>
							</>
						)}
					</Offcanvas.Body>
				</Offcanvas>
			</div>
		</>
	);
};

export default ConductAssessment;
